
import {
  ConnectionListResult,
  GridSortProps,
  PaginatedQueryWrapper,
} from '@tableau/data-catalog-shared';
import * as React from 'react';
import { Messages } from '../../../gen-src/Localize';
import { DashboardsDisplayGridRowDataFragment } from '../../../gen-src/queries/DashboardsDisplayGridRowDataFragment';
import {
  TableDownstreamDashboardsFilteredByColumnQuery,
  TableDownstreamDashboardsFilteredByColumnQuery_columns_downstreamDashboardsConnection_nodes,
  TableDownstreamDashboardsFilteredByColumnQueryVariables,
} from '../../../gen-src/queries/TableDownstreamDashboardsFilteredByColumnQuery';
import {
  TableDownstreamDashboardsQuery,
  TableDownstreamDashboardsQueryVariables,
} from '../../../gen-src/queries/TableDownstreamDashboardsQuery';
import { DashboardSortOrder } from '../../../gen-src/queries/globalTypes';
import { TableDownstreamDashboardsFilteredByColumnQueryGql } from '../../queries/TableDownstreamDashboardsFilteredByColumnQueryGql';
import { TableDownstreamDashboardsQueryGql } from '../../queries/TableDownstreamDashboardsQueryGql';
import { DashboardsDisplayGrid, DashboardsDisplayGridRowData, getRowDataForDashboard } from '../sharedDisplayGrid/DashboardsDisplayGrid';
import { DashboardsValidSortFields } from '../sorting/sortProviders/DashboardsGridSortProvider';

type QueryVariables = TableDownstreamDashboardsQueryVariables;
type QueryResults = TableDownstreamDashboardsQuery;
type QueryResultListNode = DashboardsDisplayGridRowDataFragment;
type GridRowData = DashboardsDisplayGridRowData;

type QueryVariablesWithFilter = TableDownstreamDashboardsFilteredByColumnQueryVariables;
type QueryResultsWithFilter = TableDownstreamDashboardsFilteredByColumnQuery;
type QueryResultListNodeWithFilter = TableDownstreamDashboardsFilteredByColumnQuery_columns_downstreamDashboardsConnection_nodes;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.tables && resultData.tables[0] &&
    resultData.tables[0].downstreamDashboardsConnection;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNodeWithFilter> | null {
  return resultData && resultData.columns && resultData.columns[0] &&
    resultData.columns[0].downstreamDashboardsConnection;
}

export type TableDownstreamDashboardsQueryGridProps =
  GridSortProps<DashboardsValidSortFields> & {
    readonly tableId: string;
    readonly filteredColumnId: string | null;
    readonly rowCount: number | null;
  };

export function TableDownstreamDashboardsQueryGrid(props: TableDownstreamDashboardsQueryGridProps): JSX.Element {
  const emptyGridMessage = Messages.EmptyDownstreamDashboardsLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredDashboardsLineageGridMessage();

  return props.filteredColumnId ? (
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNodeWithFilter, GridRowData>
      queryGql={TableDownstreamDashboardsFilteredByColumnQueryGql}
      partialQueryVariables={{ columnId: props.filteredColumnId, orderBy: props.orderBy as DashboardSortOrder }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForDashboard}
    >
      {({ rowStore }) => <DashboardsDisplayGrid
        rowStore={rowStore}
        emptyGridMessage={emptyFilteredGridMessage}
        rowCount={props.rowCount}
        orderBy={props.orderBy}
        updateSortOrder={props.updateSortOrder}
      />}
    </PaginatedQueryWrapper>
  ) : (
      <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
        queryGql={TableDownstreamDashboardsQueryGql}
        partialQueryVariables={{ tableId: props.tableId, orderBy: props.orderBy as DashboardSortOrder }}
        selectConnectionListResult={selectConnectionListResult}
        mapResultNode={getRowDataForDashboard}
      >
        {({ rowStore }) => <DashboardsDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />}
      </PaginatedQueryWrapper>
    );
}
